<template>
  <div :class="isWidget ? 'record-widget' : 'record-page'">
    <div v-if="!isWidget" class="record-heading"><div><h2>制作记录</h2><p>查看任务进度，预览和下载生成结果</p></div><span>{{ total }} 条记录</span></div>
    <!-- 筛选栏（页面模式） -->
    <div v-if="!isWidget" class="filter-bar">
      <div class="filter-row">
        <el-select v-model="filterType" size="small" placeholder="全部类型" clearable @change="onSearch">
          <el-option v-for="t in types" :key="t" :label="t" :value="t === '全部' ? '' : t"></el-option>
        </el-select>
        <el-input v-model="searchText" placeholder="搜索标题或ID" size="small" clearable prefix-icon="el-icon-search"
          @keyup.enter.native="onSearch" @clear="onSearch" class="search-input" />
        <el-button size="small" type="primary" @click="searchClick">搜索</el-button>
        <el-button size="small" :type="batchMode ? 'danger' : 'default'" @click="toggleBatch">批量</el-button>
        <el-button size="small" icon="el-icon-refresh" circle :loading="loading" title="刷新"
          @click="refreshRecords"></el-button>
        <span v-if="pd.zs > 0" class="queue-tag" :class="pd.zt == 1 ? 'queue-active' : 'queue-info'">
          <app-icon  class="app-icon" name="pending"></app-icon>
          <template v-if="pd.zt == 1">制作中您在第 {{ pd.wz }} 位 共 {{ pd.zs }} 个任务</template>
          <template v-else>官网 {{ pd.zs }} 个任务在制作</template>
        </span>
      </div>
    </div>

    <!-- 记录列表 -->
    <div :class="isWidget ? 'widget-body' : 'page-main'">
      <!-- 批量操作栏：放在 page-main 内，与卡片共享同一层左右内边距，天然对齐 -->
      <div v-if="!isWidget && batchMode" class="batch-bar">
        <el-checkbox :value="allSelected" @change="toggleSelectAll">全选</el-checkbox>
        <span class="batch-count">已选 {{ selectedIds.length }} 条</span>
        <el-button size="mini" type="danger" :disabled="selectedIds.length === 0" @click="batchDelete">删除所选</el-button>
        <el-button size="mini" @click="exitBatch">退出批量</el-button>
      </div>

      <div class="record-list" v-if="displayRecords.length > 0">
        <div class="record-item" v-for="item in displayRecords" :key="item.id">
          <!-- 头部行：类型 + 下载状态 + 删除 -->
          <div class="record-head">
            <div class="head-left">
              <el-checkbox v-if="batchMode" class="batch-check" :value="selectedIds.includes(item.id)"
                @change="v => toggleSelect(item.id, v)"></el-checkbox>
              <span class="head-type" v-if="item.type">{{ item.type }}</span>
              <span class="head-status" :class="'status-' + item.statusType">{{ item.statusText }}</span>
            </div>
            <button v-if="!batchMode" class="btn-icon-sm btn-delete" title="删除" @click="deleteRecord(item)">
              <app-icon  class="app-icon" name="delete_outline"></app-icon>
            </button>
          </div>

          <!-- 主行：封面 + 信息 -->
          <div class="record-main">
            <div class="record-cover" @click="preview(item)">
              <img v-if="item.cover" :src="item.cover" class="cover-img" @error="onCoverError($event, item)" />
              <div v-else class="cover-fallback" :style="{ background: getIconBg(item.type) }">
                <app-icon  class="app-icon" :name="getIcon(item.type)"></app-icon>
              </div>
            </div>

            <div class="record-info">
              <div class="record-title-row">
                <span class="record-title" :title="item.zt === 2 ? item.url : item.title">
                  {{ item.zt === 2 ? (item.url || '制作失败') : item.title }}
                </span>
                <button class="btn-icon-sm" title="复制标题" @click="copyTitle(item)">
                  <app-icon  class="app-icon" :name="copiedId === item.id ? 'check' : 'content_copy'"></app-icon>
                </button>
              </div>
              <div class="record-meta">
                <span class="record-id">视频 ID：{{ item.id }}</span>
                <span class="record-time">{{ formatTime(item.time) }}</span>
              </div>
              <div class="record-details">
                <button type="button" class="detail-chip mode-chip" :disabled="!item.zhxz"
                  :title="item.zhxz ? '点击复制完整模式：' + item.zhxz : '此记录未保存模式'" @click="copyMode(item)">
                  <i class="el-icon-setting"></i>
                  <span class="chip-label">模式</span><span class="mode-value">{{ item.zhxz || '未记录' }}</span>
                  <i v-if="item.zhxz" class="el-icon-document-copy"></i>
                </button>
                <span class="detail-chip points-chip"><i class="el-icon-coin"></i><span class="chip-label">扣点</span>
                  <strong>{{ item.zt === 3 ? '待结算' : (item.kcds || 0) + ' 点' }}</strong></span>
                <span v-if="item.spsj" class="detail-chip duration-chip"><i class="el-icon-time"></i><span class="chip-label">时长</span>
                  <strong>{{ item.spsj }} 秒</strong></span>
              </div>
            </div>
          </div>

          <!-- 操作行：原链接 + 下载（靠右） -->
          <div class="record-bar" v-if="item.yurl || item.zt === 0 || item.zt === 1">
            <button class="bar-btn bar-copy" v-if="item.yurl" @click="copyYurl(item)">
              <app-icon  class="app-icon" name="link"></app-icon>
              <span>原链接</span>
            </button>
            <button class="bar-btn bar-download" :class="{ 'is-downloaded': item.zt === 0 }"
              v-if="item.zt === 0 || item.zt === 1" @click="download(item)">
              <app-icon  class="app-icon" name="download"></app-icon>
              <span>下载文件</span>
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="loading" class="record-skeleton" aria-busy="true" aria-label="正在加载制作记录"><div v-for="n in 3" :key="n" class="skeleton-record"><span></span><div><i></i><i></i></div></div></div>
      <div v-else class="empty-state">
        <app-icon  class="app-icon" name="inbox"></app-icon>
        <p>{{ searchText || filterType ? '没有找到匹配的记录' : '暂无制作记录' }}</p>
      </div>

      <!-- 组件模式：查看全部 -->
      <button v-if="isWidget" class="btn-view-all" @click="$router.push('/user2/record')">查看全部</button>

      <!-- 页面模式：分页 -->
      <div v-if="!isWidget && total > 0" class="pagination">
        <el-pagination background :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next'" :pager-count="isMobile ? 5 : 7" :page-sizes="[20, 50, 100]"
          :page-size="pageSize" :total="total" :current-page="currentPage" @current-change="onPageChange"
          @size-change="onSizeChange" />
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog :visible.sync="previewVisible" title="视频预览" append-to-body width="640px" :close-on-click-modal="true">
      <div class="preview-wrapper" v-if="previewItem">
        <video v-if="isVideoUrl(previewUrl)" :src="previewUrl" controls autoplay class="preview-video"></video>
        <img v-else :src="previewUrl" class="preview-img" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { copyText } from '@/common.js'

const TYPE_ICONS = {
  '视频制作': { icon: 'movie_edit', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  '图文生成': { icon: 'image', bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
  '视频转文字': { icon: 'text_fields', bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  '语音合成': { icon: 'mic', bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  'AI数字人': { icon: 'smart_toy', bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
}
const DEFAULT_ICON = { icon: 'description', bg: 'linear-gradient(135deg, #a0a0a0 0%, #c0c0c0 100%)' }

export default {
  name: 'RecordPage',
  props: {
    limit: { type: Number, default: 0 }
  },
  data() {
    return {
      searchText: '',
      filterType: '',
      types: ['全部'],
      currentPage: 1,
      pageSize: 50,
      total: 0,
      records: [],
      isMobile: window.innerWidth <= 640,
      loading: false,
      copiedId: null,
      previewVisible: false,
      previewItem: null,
      previewUrl: '',
      pd: { zt: 0, wz: 0, zs: 0 },
      batchMode: false,
      selectedIds: [],
      lastRefreshAt: 0
    }
  },
  computed: {
    isWidget() {
      return this.limit > 0
    },
    displayRecords() {
      return this.isWidget ? this.records.slice(0, this.limit) : this.records
    },
    allSelected() {
      return this.displayRecords.length > 0 && this.selectedIds.length === this.displayRecords.length
    }
  },
  methods: {
    toggleBatch() {
      this.batchMode = !this.batchMode
      if (!this.batchMode) this.selectedIds = []
    },
    exitBatch() {
      this.batchMode = false
      this.selectedIds = []
    },
    toggleSelect(id, checked) {
      if (checked) {
        if (!this.selectedIds.includes(id)) this.selectedIds.push(id)
      } else {
        this.selectedIds = this.selectedIds.filter(x => x !== id)
      }
    },
    toggleSelectAll(checked) {
      this.selectedIds = checked ? this.displayRecords.map(r => r.id) : []
    },
    refreshRecords() {
      const now = Date.now()
      if (now - this.lastRefreshAt < 3000) {
        this.$msg.warning('操作过于频繁，请稍后再试')
        return
      }
      this.lastRefreshAt = now
      this.loadRecords()
      this.getPaidui()
    },
    batchDelete() {
      if (this.selectedIds.length === 0) return
      this.$confirm(`确定删除选中的 ${this.selectedIds.length} 条记录吗？`, '批量删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$ajax.post('api/v1/users/record/batch-delete', { ids: this.selectedIds }).then(res => {
          if (res.data && res.data.status === 200) {
            const deleted = res.data.deleted || 0
            this.records = this.records.filter(r => !this.selectedIds.includes(r.id))
            this.total = Math.max(0, this.total - deleted)
            this.$msg.success(res.data.msg || '删除成功')
            this.selectedIds = []
          } else {
            this.$msg.error((res.data && res.data.msg) || '删除失败')
          }
        }).catch(() => {
          this.$msg.error('删除失败，请稍后重试')
        })
      }).catch(() => { })
    },
    loadRecords() {
      this.loading = true
      const params = {
        page: this.currentPage,
        pagesize: this.isWidget ? (this.limit || 5) : this.pageSize,
      }
      if (this.searchText) params.find = this.searchText
      if (this.filterType) params.type = this.filterType

      this.$ajax.get('api/v1/users/record', { params }).then(res => {
        const body = res.data
        if (body && body.status === 200) {
          this.records = (body.data && body.data.list) ? body.data.list : []
          this.total = (body.data && body.data.total) ? body.data.total : 0
          if (body.types && body.types.length > 0) {
            this.types = body.types
          }
        } else {
          this.records = []
          this.total = 0
        }
      }).catch(() => {
        this.records = []
        this.total = 0
      }).finally(() => {
        this.loading = false
      })
    },
    onSearch() {
      this.currentPage = 1
      this.loadRecords()
    },
    searchClick() {
      if (!this.searchText.trim()) {
        this.$msg.warning('请输入搜索内容')
        return
      }
      this.onSearch()
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadRecords()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadRecords()
    },
    getPaidui() {
      this.$ajax.get('api/v1/users/paidui').then(res => {
        if (res.data && res.data.status === 200 && res.data.pd) {
          this.pd = res.data.pd
          if (res.data.pd.zt == 1) {
            // 有排队任务：5 秒轮询，直到没有排队
            if (!this.pdTimer) {
              this.pdTimer = setInterval(() => this.getPaidui(), 5000)
            }
          } else {
            this.stopPaidui()
          }
        }
      }).catch(() => { })
    },
    stopPaidui() {
      if (this.pdTimer) {
        clearInterval(this.pdTimer)
        this.pdTimer = null
      }
    },
    getIcon(type) {
      return (TYPE_ICONS[type] || DEFAULT_ICON).icon
    },
    getIconBg(type) {
      return (TYPE_ICONS[type] || DEFAULT_ICON).bg
    },
    formatTime(ts) {
      if (!ts) return ''
      const d = new Date(ts * 1000)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    copyTitle(item) {
      const text = item.title || ''
      if (!text) return
      copyText(text).then(ok => {
        if (ok) {
          this.copiedId = item.id
          this.$msg.success('已复制标题')
          setTimeout(() => { this.copiedId = null }, 2000)
        } else {
          this.$msg.error('复制失败')
        }
      })
    },
    copyMode(item) {
      if (!item.zhxz) return
      copyText(item.zhxz).then(ok => {
        if (ok) this.$msg.success('已复制完整模式')
        else this.$msg.error('复制失败')
      })
    },
    copyYurl(item) {
      const url = item.yurl || ''
      if (!url) {
        this.$msg.info('暂无链接')
        return
      }
      copyText(url).then(ok => {
        if (ok) {
          this.$msg.success('已复制原链接')
        } else {
          this.$msg.error('复制失败')
        }
      })
    },
    copyUrl(item) {
      const url = item.url || ''
      if (!url) {
        this.$msg.info('暂无链接')
        return
      }
      copyText(url).then(ok => {
        if (ok) {
          this.$msg.success('已复制链接')
        } else {
          this.$msg.error('复制失败')
        }
      })
    },
    download(item) {
      const url = item.url || item.yurl || ''
      if (!url) {
        this.$msg.info('暂无下载链接')
        return
      }
      if (item.zt === 2) return
      // 本地直接标记已下载（zt=0），不重新请求列表
      item.zt = 0
      item.statusText = '已下载'
      item.statusType = 'success'
      // 请求新后端接口持久化状态（zt=0，down_num+1）
      this.$ajax.post(`api/v1/users/record/${item.id}/downloaded`).catch(() => { })
      window.open(url)
    },
    deleteRecord(item) {
      this.$confirm('确定删除该记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$ajax.delete(`api/v1/users/record/${item.id}`).then(res => {
          if (res.data && res.data.status === 200) {
            this.$msg.success('已删除')
            this.records = this.records.filter(r => r.id !== item.id)
            this.total = Math.max(0, this.total - 1)
          } else {
            this.$msg.error('删除失败')
          }
        }).catch(() => {
          this.$msg.error('删除失败，请稍后重试')
        })
      }).catch(() => { })
    },
    preview(item) {
      const url = item.url || item.yurl || item.cover || ''
      if (!url) return
      if (this.isVideoUrl(url)) {
        this.previewUrl = url
        this.previewItem = item
        this.previewVisible = true
      } else if (item.cover) {
        this.previewUrl = item.cover
        this.previewItem = item
        this.previewVisible = true
      }
    },
    isVideoUrl(url) {
      return /\.(mp4|mov|avi|webm|m3u8|ts)(\?|$)/i.test(url)
    },
    onCoverError(e, item) {
      // 封面加载失败，回退到类型图标
      e.target.style.display = 'none'
    }
  },
  created() {
    this.loadRecords()
    this.getPaidui()
  },
  mounted() {
    this._onViewportResize = () => { this.isMobile = window.innerWidth <= 640 }
    window.addEventListener('resize', this._onViewportResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onViewportResize)
    this.stopPaidui()
  }
}
</script>

<style scoped>
/* ===== 封面 ===== */
.record-cover {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  background: #f1f5f9;
}

.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-fallback .app-icon {
  font-size: 24px;
  color: #ffffff;
}

/* ===== 信息区 ===== */
.record-info {
  flex: 1;
  min-width: 0;
}

.record-title-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.record-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.record-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
  align-items: center;
}

.record-id,
.record-time,
.record-type,
.record-size {
  font-size: 12px;
  color: #6b7280;
}

.record-type {
  background: #f1f5f9;
  padding: 0 6px;
  border-radius: 4px;
}

/* ===== 操作按钮 ===== */
.btn-icon-sm {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
}

.btn-icon-sm .app-icon {
  font-size: 16px;
}

.btn-icon-sm:hover {
  border-color: #0052ff;
  color: #0052ff;
}

.btn-download:hover {
  border-color: #10b981;
  color: #10b981;
}

.btn-delete:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: #fee2e2;
}

/* ===== 预览 ===== */
.preview-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  min-height: 300px;
}

.preview-video {
  width: 100%;
  max-height: 480px;
}

.preview-img {
  max-width: 100%;
  max-height: 480px;
  object-fit: contain;
}

/* ===== 空状态 ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.empty-state .app-icon {
  font-size: 40px;
  opacity: 0.4;
  margin-bottom: 8px;
}

.empty-state p {
  font-size: 13px;
  margin: 0;
}

/* ===== 组件模式 ===== */
.record-widget .widget-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record-widget .record-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 8px;
}

.record-widget .record-head {
  margin-bottom: 0;
}

.record-widget .record-bar {
  margin-top: 0;
  padding-top: 6px;
}

.record-widget .record-cover {
  width: 40px;
  height: 40px;
  border-radius: 6px;
}

.record-widget .btn-view-all {
  padding: 8px;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.record-widget .btn-view-all:hover {
  border-color: #0052ff;
  color: #0052ff;
}

/* ===== 页面模式 ===== */
.record-page {
  --c-bg: #f5f7fa;
  --c-card: #ffffff;
  --c-text: #1a1a2e;
  --c-border: #e5e7eb;
  font-family: 'Inter', -apple-system, sans-serif;
}

.record-page .filter-bar {
    padding: 16px 24px;
    background: var(--c-card);
    border-bottom: 1px solid var(--c-border);
  }

  .record-page .batch-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    /* 与卡片同宽：它在 .page-main 内，只留与 .record-list 相同的 10px 间距 */
    margin-bottom: 10px;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid var(--c-border);
    border-radius: 10px;
  }

  .batch-count {
    color: #64748b;
    font-size: 13px;
  }

  .batch-check {
    margin-right: 8px;
  }

  .queue-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  margin-left: auto;
  white-space: nowrap;
}

.queue-tag .app-icon {
  font-size: 17px;
}

.queue-active {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.queue-info {
  background: #f0f5ff;
  color: #1a73e8;
  border: 1px solid #dbeafe;
}

.filter-row {
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: 900px;
    margin: 0 auto;
}

.search-input {
  width: 260px;
}

.record-page .page-main {
  max-width: 900px;
  margin: 0 auto;
  padding: 14px;
}

.record-page .record-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record-page .record-item {
  display: flex;
  flex-direction: column;
  padding: 14px 18px;
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  transition: all 0.2s;
}

.record-page .record-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* 头部行：类型 + 状态 + 删除 */
.record-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.head-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.head-type {
  font-size: 11px;
  font-weight: 600;
  color: #0052ff;
  background: #e3f2fd;
  padding: 2px 8px;
  border-radius: 4px;
}

.head-status {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
}

.head-status.status-success {
  color: #059669;
  background: #d1fae5;
}

.head-status.status-warning {
  color: #d97706;
  background: #fef3c7;
}

.head-status.status-danger {
  color: #dc2626;
  background: #fee2e2;
}

.head-status.status-info {
  color: #6b7280;
  background: #f1f5f9;
}

/* 主行：封面 + 信息 */
.record-main {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 操作栏：原链接 + 下载（靠右） */
.record-bar {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
}

.bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.bar-btn .app-icon {
  font-size: 14px;
}

.bar-copy {
  background: #f1f5f9;
  color: #64748b;
}

.bar-copy:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.bar-download {
  background: #0052ff;
  color: #ffffff;
}

.bar-download:hover {
  background: #003ec7;
}

.bar-download.is-downloaded,
.bar-download.is-downloaded:hover {
  background: #e2e8f0;
  color: #64748b;
}

.pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

/* ===== 响应式 ===== */
@media (max-width: 640px) {
    .filter-row {
      flex-wrap: wrap;
      gap: 8px;
    }

    .filter-row .el-select {
      width: 100%;
    }

    .filter-row .el-select .el-input {
      width: 100%;
    }

    .search-input {
      flex: 1;
      min-width: 140px;
      width: auto;
    }

    .filter-row > .el-button {
      margin: 0;
    }

    .queue-tag {
      margin-left: 0;
      white-space: normal;
      font-size: 12px;
    }

    .record-page .batch-bar {
      flex-wrap: wrap;
      gap: 8px;
    }

    .record-page .page-main {
      padding: 16px;
    }

    .pagination {
      justify-content: center;
    }

    .record-cover {
      width: 44px;
      height: 44px;
    }

    .record-main {
      flex-wrap: wrap;
      gap: 10px;
    }

    .record-bar {
      flex-wrap: wrap;
    }
  }

.record-heading { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:24px 24px 18px; }
.record-heading h2 { margin:0 0 6px; font-size:24px; color:#0f172a; }
.record-heading p { margin:0; color:#64748b; font-size:13px; }
.record-heading>span { color:#94a3b8; font-size:12px; white-space:nowrap; }
.skeleton-record { display:flex; gap:16px; padding:24px; margin-bottom:12px; border:1px solid #e2e8f0; border-radius:14px; }
.skeleton-record>span { width:52px; height:52px; border-radius:10px; background:#e2e8f0; }
.skeleton-record>div { flex:1; }
.skeleton-record i { display:block; height:12px; width:70%; margin:8px 0 14px; border-radius:4px; background:#f1f5f9; }
.skeleton-record i:last-child { width:40%; }
@media(max-width:640px) {
 .record-heading { padding:18px 16px 12px; }
 .record-heading h2 { font-size:22px; }
 .filter-row { display:grid; grid-template-columns:minmax(0,1fr) auto auto auto; }
 .filter-row .el-select { grid-column:1/-1; }
 .filter-row .search-input { min-width:0; width:100%; }
 .filter-row .queue-tag { grid-column:1/-1; }
 .record-title { white-space:normal; overflow-wrap:anywhere; line-height:1.6; }
 .head-status { font-weight:600; }
 .bar-btn { min-height:40px; }
 .pagination { overflow-x:auto; justify-content:flex-start; }
}

/* 模式、实际扣点和视频时长独立展示。 */
.record-details {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-top: 10px;
}
.detail-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  min-height: 32px;
  padding: 6px 10px;
  box-sizing: border-box;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.5;
}
.chip-label { flex-shrink: 0; opacity: .75; }
.detail-chip > i { flex-shrink: 0; font-size: 14px; }
.detail-chip strong { font-weight: 600; white-space: nowrap; }
.mode-chip {
  max-width: 360px;
  background: #eff6ff;
  border-color: #dbeafe;
  color: #2563eb;
  cursor: pointer;
  text-align: left;
}
.mode-chip:hover:not(:disabled) { background: #dbeafe; border-color: #93c5fd; }
.mode-chip:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.mode-chip:disabled { background: #f8fafc; border-color: #e2e8f0; color: #94a3b8; cursor: default; }
.mode-value { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.points-chip { background: #fffbeb; border-color: #fde68a; color: #a16207; }
.duration-chip { background: #f1f5f9; border-color: #e2e8f0; color: #475569; }
.record-page .record-cover { width: 72px; height: 104px; border-radius: 10px; }
@media (max-width: 640px) {
  .record-page .record-cover { width: 60px; height: 88px; }
  .record-page .record-main { flex-wrap: nowrap; align-items: flex-start; }
  .record-details { gap: 6px; margin-top: 8px; }
  .detail-chip { padding: 5px 8px; font-size: 11px; }
  .mode-chip { max-width: 100%; }
}
</style>



// WEBPACK FOOTER //
// src/components/User2/Record.vue